import React, { useState, useCallback, useMemo, useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import ThemeContext from '../contexts/ThemeContext';
import i18n from '../i18n';
import sortIcon from '../images/sortIcon.svg';

const TableContainer = styled.div`
  padding: 0 20px 20px;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  letter-spacing: 0.5px;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.15px;
  color: ${({ color }) => color};
  cursor: pointer;
  user-select: none;
  white-space: nowrap;

  &:hover {
    opacity: 0.7;
  }
`;

const SortIcon = styled.span`
  margin-left: 4px;
  display: inline-flex;
  vertical-align: middle;
`;

const Tr = styled.tr`
  border-bottom: 1px solid ${({ borderColor }) => borderColor || 'rgba(0,0,0,0.1)'};
`;

const Td = styled.td`
  padding: 14px 16px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
  color: ${({ color }) => color};
  white-space: nowrap;
`;

const OpenButton = styled.button`
  background: transparent;
  border: 1px solid ${({ color }) => color};
  border-radius: 20px;
  color: ${({ color }) => color};
  padding: 6px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  letter-spacing: 0.5px;
  white-space: nowrap;

  &:hover {
    background-color: ${({ color }) => color};
    color: white;
  }
`;

const getAriaSortValue = (sortField, sortDirection, colKey) => {
  if (sortField !== colKey) return 'none';
  return sortDirection === 'asc' ? 'ascending' : 'descending';
};

const TranslationsTable = ({ columns, data, onRowOpen }) => {
  const { colors } = useContext(ThemeContext);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = useCallback((field) => {
    setSortDirection(prev => sortField === field && prev === 'asc' ? 'desc' : 'asc');
    setSortField(field);
  }, [sortField]);

  const handleKeyDown = useCallback((e, field) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSort(field);
    }
  }, [handleSort]);

  const sortAccessor = useMemo(() => {
    if (!sortField) return null;
    const col = columns.find(c => c.key === sortField);
    return col?.sortKey || sortField;
  }, [columns, sortField]);

  const sortedData = useMemo(() => {
    if (!sortField) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortAccessor];
      const bVal = b[sortAccessor];
      const aMissing = aVal === null || aVal === undefined;
      const bMissing = bVal === null || bVal === undefined;
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      const cmp = typeof aVal === 'string' ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [data, sortField, sortAccessor, sortDirection]);

  return (
    <TableContainer>
      <Table>
        <thead>
          <Tr borderColor={colors.webDeviderColor}>
            {columns.map((col) => (
              <Th
                key={col.key}
                color={colors.webDarkBlue}
                onClick={() => handleSort(col.key)}
                onKeyDown={(e) => handleKeyDown(e, col.key)}
                role="button"
                tabIndex={0}
                aria-sort={getAriaSortValue(sortField, sortDirection, col.key)}
              >
                {col.label}
                <SortIcon>
                  <img src={sortIcon} alt="" width={18} height={18} />
                </SortIcon>
              </Th>
            ))}
            <Th color={colors.webDarkBlue} />
          </Tr>
        </thead>
        <tbody>
          {sortedData.map((row) => (
            <Tr key={row.id} borderColor={colors.webDeviderColor}>
              {columns.map((col) => (
                <Td key={col.key} color={colors.webDarkBlue}>
                  {col.render ? col.render(row[col.key], row) : <span>{String(row[col.key] ?? '')}</span>}
                </Td>
              ))}
              <Td>
                <OpenButton
                  color={colors.tulkaMainColor}
                  onClick={() => onRowOpen(row)}
                >
                  {i18n('open')}
                </OpenButton>
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </TableContainer>
  );
};

TranslationsTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    render: PropTypes.func,
  })).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRowOpen: PropTypes.func.isRequired,
};

export default TranslationsTable;
